<template>
  <div>
    <section :class="$style.availableOsUpdatesContainer">
      <ul :class="$style.infoList">
        <li :class="$style.infoListItem">
          <span>OS</span>
          <span>{{ version }}</span>
        </li>
        <li :class="$style.infoListItem">
          <span>Total Number of Versions Available</span>
          <span>{{ numAvailableOsUpdates }}</span>
        </li>
        <li :class="$style.infoListItem">
          <span>Last Scanned for Updates</span>
          <span>{{ updatedAt }}</span>
        </li>
      </ul>
    </section>
    <div v-if="hasAvailableOsUpdates">
      <div :class="$style.availableOsUpdatesContainer">
        <HeadingExtraSmall
          :class="$style.availableOsUpdatesHeader"
          element="h5"
          hasMargin
          name="availableOsUpdatesHeader"
        >
          Select an OS Update to Schedule
        </HeadingExtraSmall>
        <NotificationInPage
          :notification="notificationConfig"
          :showNotification="hasMajorUpdates"
        />
        <div :class="['jc-scrollbar-vertical', $style.availableOsUpdates]">
          <RadioGroup
            v-model="selectedUpdate"
            :class="$style.availableOsUpdatesOptions"
            :inline="false"
            :isMarkdown="false"
            label="Available Updates"
            name="availableOsUpdatesRadioGroup"
            :options="availableOsUpdates"
            :required="false"
            showValidationMessage
            @input="updateSelected"
          />
        </div>

        <div :class="$style.scheduleSelect">
          <Select
            v-model="selectedInstallAction"
            :isDisabled="installActionDisabled"
            label="Install Action"
            name="schedule"
            :options="scheduleOptions"
            placeholder="Select Install Action"
            @input="installActionSelected"
          />
        </div>
        <div :class="$style.scheduleButton">
          <ButtonSecondary
            :isDisabled="scheduleButtonDisabled"
            text="Schedule..."
            @click="scheduleUpdate"
          />
        </div>
        <SeverityModal
          ref="scheduleModal"
          :checkboxLabel="modalCheckboxLabel"
          :notificationMessage="notificationMessage"
          :notificationTitle="notificationTitle"
          :showCloseButton="true"
          :showModal="showModal"
          :title="modalTitle"
          :useCheckbox="modalCheckbox"
          :variant="severity"
          @cancel="cancelModal"
        >
          <template v-slot:subtitle>
            <span
              :data-test-id="$testId('subtitle')"
            >{{ subtitle }}</span>
          </template>
          <template v-slot:body>
            <ParagraphBody
              :data-test-id="$testId('modalBody')"
            >
              {{ modalBody }}
            </ParagraphBody>
          </template>
          <template v-slot:learnmore>
            <ParagraphBody>
              For more information visit,
              <Link
                :href="helpLink"
                isExternal=""
                target="_blank"
                text="Schedule OS Update for Mac Devices"
              />
            </ParagraphBody>
          </template>
          <template v-slot:footer>
            <ButtonGroup
              :isFullWidth="false"
            >
              <ButtonSecondary
                text="cancel"
                @click="cancelModal"
              />
              <ButtonSecondary
                v-if="modalCustomBtn"
                :isDisabled="disabledUpdateNowModalBtn"
                :text="modalConfirmBtn"
                type="error"
                @click="confirmUpdate"
              />
              <Button
                v-else
                :data-test-id="$testId('modalConfirm')"
                text="Schedule Update"
                @click="confirmUpdate"
              />
            </ButtonGroup>
          </template>
        </SeverityModal>
      </div>
    </div>
  </div>
</template>

<script>
import {
  Button,
  ButtonGroup,
  ButtonSecondary,
  HeadingExtraSmall,
  Link,
  NotificationInPage,
  ParagraphBody,
  RadioGroup,
  Select,
  SeverityModal,
} from '@jumpcloud/ui-components';
import { find, get } from 'lodash';
import { mapActions } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import Constants, { ItemTypeConsts, StoreModelMap } from 'client/util/Constants';
import MdmScheduleOsUpdateConstants from 'client/MDM/constants/MdmScheduleOsUpdateConstants';
import moment from 'moment';

const { modalMap, scheduleOptionsMap } = MdmScheduleOsUpdateConstants;
const { dateTimeLongFormat } = Constants;

const mdmSettingsModel = StoreModelMap[ItemTypeConsts.mdmSettings];

export default {
  name: 'MdmScheduleOsUpdate',
  components: {
    Button,
    ButtonGroup,
    ButtonSecondary,
    Link,
    NotificationInPage,
    ParagraphBody,
    HeadingExtraSmall,
    RadioGroup,
    Select,
    SeverityModal,
  },

  props: {
    deviceId: {
      type: String,
      default: '',
    },
    version: {
      type: String,
      default: 'Unknown',
    },
  },

  data: () => ({
    availableOsUpdates: [],
    numAvailableOsUpdates: 0,
    updatedAt: 'Unknown',
    errorMsg: '',
    errorStatus: '',
    helpLink: 'https://support.jumpcloud.com/s/article/Schedule-an-OS-Update-for-a-MacOS-Device',
    installActionDisabled: true,
    notificationConfig: {
      component: undefined,
      emoji: undefined,
      id: undefined,
      icon: 'info',
      isMarkdown: false,
      isSingleLineAction: false,
      message: 'Major updates can only be scheduled as install ASAP.',
      showCloseButton: false,
      title: '',
      type: 'informational',
    },
    notificationMessage: '',
    notificationTitle: '',
    modalBody: '',
    modalTitle: '',
    modalCheckbox: false,
    modalCheckboxLabel: '',
    modalConfirmBtn: '',
    modalCustomBtn: false,
    scheduleButtonDisabled: true,
    scheduleOptions: [
      scheduleOptionsMap.INSTALL_LATER,
      scheduleOptionsMap.INSTALL_ASAP,
      scheduleOptionsMap.INSTALL_FORCE_RESTART,
    ],
    selectedInstallAction: '',
    selectedUpdate: '',
    severity: 'sev2',
    showModal: false,
    subtitle: '',
  }),
  computed: {
    disabledUpdateNowModalBtn() {
      return !this.$refs.scheduleModal.checked;
    },

    hasAvailableOsUpdates() {
      return this.numAvailableOsUpdates !== 0;
    },

    hasMajorUpdates() {
      const hasMajorUpdatesInList = this.availableOsUpdates.filter(obj => obj.isMajorOSUpdate);
      return hasMajorUpdatesInList.length > 0;
    },
  },
  async mounted() {
    await this.retrieveAvailableOSUpdates();
  },

  methods: {
    ...mapActions(notificationModule, [
      'triggerErrorNotification',
      'triggerSuccessNotification',
    ]),
    ...mapActions(mdmSettingsModel, [
      'fetchAvailableOSUpdates',
      'postScheduleOSUpdate',
    ]),

    cancelModal() {
      this.showModal = false;
    },
    confirmUpdate() {
      const { deviceId, selectedInstallAction, selectedUpdate } = this;
      try {
        this.postScheduleOSUpdate({
          deviceId,
          selectedInstallAction,
          selectedUpdate,
        });
        this.triggerSuccessNotification({
          message: 'OS Update scheduled!',
          showCloseButton: true,
        });
      } catch (error) {
        this.errorMsg = get(error, 'response.data.message', '');
        this.errorStatus = get(error, 'response.status');
        this.triggerErrorNotification({
          message: 'The OS update could not be scheduled at this time. Please try again later.',
          showCloseButton: true,
        });
      } finally {
        this.showModal = false;
      }
    },
    formatAvailableOsUpdatesLabel(update) {
      // removes version from string e.g. macOS Monterey 12.6.1 -> macOS Monterey
      const filteredHumanReadableName = update.humanReadableName.replace(/[\d.]+/g, '').trim();
      const updateType = update.isMajorOSUpdate ? 'Major' : 'Minor';
      return `${filteredHumanReadableName} ${update.version} (${updateType}) ${update.build}`;
    },

    formatUpdatedAt(updatedAt) {
      return moment(updatedAt).format(dateTimeLongFormat);
    },

    installActionSelected() {
      this.scheduleButtonDisabled = false;
    },

    mapAvailableOsUpdates(fetchedAvailableOsUpdates) {
      const availableOsUpdates = get(fetchedAvailableOsUpdates, 'availableOsUpdates.updates');

      return availableOsUpdates.reduce((updates, update) => {
        const unique = !updates.map(u => u.value).includes(update.productKey);
        if (
          !update.isConfigDataUpdate
          && update.humanReadableName !== 'Safari'
          && unique
          && update.version >= '11.0.0'
        ) {
          updates.push({
            allowsInstallLater: update.allowsInstallLater,
            build: update.build,
            isMajorOSUpdate: update.isMajorOSUpdate,
            label: this.formatAvailableOsUpdatesLabel(update),
            value: update.productKey,
          });
        }
        return updates;
      }, []).reverse();
    },

    resetInstallAction() {
      // resets the install action when a selected update no longer
      // supports the previously selected action
      this.scheduleButtonDisabled = true;
      this.selectedInstallAction = '';
    },

    async retrieveAvailableOSUpdates() {
      const { deviceId } = this;
      try {
        const fetchedAvailableOsUpdates = await this.fetchAvailableOSUpdates({ deviceId });
        this.availableOsUpdates = this.mapAvailableOsUpdates(fetchedAvailableOsUpdates);
        this.numAvailableOsUpdates = this.availableOsUpdates.length;
        this.updatedAt = this.formatUpdatedAt(fetchedAvailableOsUpdates.updatedAt);
      } catch (error) {
        // Not sure how we want to handle errors here
        // NotFound indicates there are no available os updates stored for the device
        this.errorMsg = get(error, 'response.data.message', '');
        this.errorStatus = get(error, 'response.status');
      }
    },

    scheduleUpdate() {
      this.notificationTitle = modalMap[this.selectedInstallAction].notificationTitle;
      this.notificationMessage = modalMap[this.selectedInstallAction].notificationMessage;
      this.modalBody = modalMap[this.selectedInstallAction].modalBody;
      this.modalTitle = modalMap[this.selectedInstallAction].modalTitle;
      this.modalCheckbox = modalMap[this.selectedInstallAction].modalCheckbox;
      this.modalCheckboxLabel = modalMap[this.selectedInstallAction].modalCheckboxLabel;
      this.modalConfirmBtn = modalMap[this.selectedInstallAction].modalConfirmBtn;
      this.modalCustomBtn = modalMap[this.selectedInstallAction].modalCustomBtn || false;
      const formattedSelectedUpdate = find(
        this.availableOsUpdates, { value: this.selectedUpdate },
      ) || {};
      this.subtitle = `${modalMap[this.selectedInstallAction].subtitle} ${formattedSelectedUpdate.label || ''} ${modalMap[this.selectedInstallAction].subtitle2}?`;
      this.severity = modalMap[this.selectedInstallAction].severity;

      this.showModal = true;
    },

    updateSelected(update) {
      const selectedUpdate = find(
        this.availableOsUpdates, { value: update },
      ) || {};
      const installLaterIndex = this.scheduleOptions.findIndex(object => object.value === 'INSTALL_LATER');
      const forceIndex = this.scheduleOptions.findIndex(object => object.value === 'INSTALL_FORCE_RESTART');

      if (!selectedUpdate.allowsInstallLater) {
        this.scheduleOptions[installLaterIndex].disabled = true;
        if (this.selectedInstallAction === 'INSTALL_LATER') {
          this.resetInstallAction();
        }
      } else if (selectedUpdate.isMajorOSUpdate || !selectedUpdate.build) {
        this.scheduleOptions[installLaterIndex].disabled = true;
        this.scheduleOptions[forceIndex].disabled = true;
        if (this.selectedInstallAction === 'INSTALL_LATER' || this.selectedInstallAction === 'INSTALL_FORCE_RESTART') {
          this.resetInstallAction();
        }
      } else {
        this.scheduleOptions.forEach((_option, index) => {
          this.scheduleOptions[index].disabled = false;
        });
      }
      this.installActionDisabled = false;
    },
  },
};
</script>

<style module>
.scheduleSelect {
  --input-wrapper-width: 45ch;

  display: inline-block;
}

.scheduleButton {
  display: inline-block;
  padding-left: 1rem;
}

.availableOsUpdatesContainer {
  display: inline-block;
  margin-bottom: var(--jc-spacer);
  width: 100%;
}

@value (gradientDark2, jcGrey200) from '~css/brand-colors.css';

.availableOsUpdates {
  border: 1px solid var(--jcGrey200);
  display: inline-block;
  padding: var(--jc-spacer);
  width: 100%;
}

.availableOsUpdatesHeader {
  color: gradientDark2;
  margin-bottom: var(--jc-spacer);
}

.availableOsUpdatesOptions {
  max-height: 26rem;
}

.infoListItem {
  display: flex;
  font-size: 1rem;
  justify-content: space-between;
  margin: var(--jc-spacer) 0;
}

.infoList:first-child {
  padding: 0 var(--jc-spacer) 0 0;
}

.infoListItem > span:first-child {
  font-weight: bold;
}

</style>
